import {
    Alert,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { observer } from "mobx-react";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    AgentSkill,
    AgentSkillTestCase,
    AgentSkillTestRunResponse,
    SaveAgentSkillRequest,
} from "components/AgentSkills/Models/AgentSkill";
import AgentSkillsStore from "components/AgentSkills/Stores/AgentSkillsStore";
import AgentSkillToolCallDisclosure, {
    getToolCallName,
} from "components/AgentSkills/Views/AgentSkillToolCallDisclosure";
import AcxButton from "components/UI/AcxButton";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { useStore } from "utils/useStore";

interface Props {
    open: boolean;
    skillId?: string;
    skill: SaveAgentSkillRequest;
    onClose: () => void;
    onTestCasesChange: (testCases: AgentSkillTestCase[]) => void;
    onVersionSaved: (skill: AgentSkill) => void;
}

type TestMode = "prompt" | "dataset";

const cloneTestCases = (
    testCases: AgentSkillTestCase[],
): AgentSkillTestCase[] => testCases.map((testCase) => ({ ...testCase }));

const AgentSkillTestDialog: React.FC<Props> = observer(
    ({ open, skillId, skill, onClose, onTestCasesChange, onVersionSaved }) => {
        const store = useStore(AgentSkillsStore);
        const inlineTestAbortRef = useRef<AbortController | null>(null);
        const wasOpenRef = useRef(false);
        const [mode, setMode] = useState<TestMode>("prompt");
        const [prompt, setPrompt] = useState("");
        const [inlineTesting, setInlineTesting] = useState(false);
        const [thinking, setThinking] = useState<string[]>([]);
        const [response, setResponse] = useState("");
        const [toolCalls, setToolCalls] = useState<
            Record<string, unknown>[]
        >([]);
        const [inlineTestError, setInlineTestError] = useState<string | null>(
            null,
        );
        const [testResults, setTestResults] =
            useState<AgentSkillTestRunResponse | null>(null);
        const [savedTestCases, setSavedTestCases] = useState<
            AgentSkillTestCase[]
        >([]);
        const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

        const savingDataset = store.getTaskLoading(
            AgentSkillsStore.Tasks.SaveTestDataset,
        );
        const testingDataset = store.getTaskLoading(
            AgentSkillsStore.Tasks.TestSkill,
        );

        const testCases = useMemo(
            () => skill.testCases ?? [],
            [skill.testCases],
        );
        const datasetDirty =
            JSON.stringify(testCases) !== JSON.stringify(savedTestCases);
        const datasetValid = testCases.every(
            (testCase) =>
                testCase.userRequest.trim().length > 0 &&
                testCase.idealOutcome.trim().length > 0,
        );
        const canTestSkill =
            skill.name.trim().length > 0 &&
            skill.description.trim().length > 0 &&
            skill.skillText.trim().length > 0;
        const hasScopes = skill.scopes.length > 0;

        useEffect(
            () => () => {
                inlineTestAbortRef.current?.abort();
            },
            [],
        );

        useEffect(() => {
            if (open && !wasOpenRef.current) {
                setSavedTestCases(cloneTestCases(testCases));
                setTestResults(null);
                setConfirmDiscardOpen(false);
            }
            if (!open) {
                setConfirmDiscardOpen(false);
            }
            wasOpenRef.current = open;
        }, [open, testCases]);

        const closeImmediately = useCallback(() => {
            inlineTestAbortRef.current?.abort();
            setConfirmDiscardOpen(false);
            onClose();
        }, [onClose]);

        const requestClose = useCallback(() => {
            if (datasetDirty) {
                setConfirmDiscardOpen(true);
                return;
            }
            closeImmediately();
        }, [closeImmediately, datasetDirty]);

        const discardAndClose = useCallback(() => {
            onTestCasesChange(cloneTestCases(savedTestCases));
            closeImmediately();
        }, [closeImmediately, onTestCasesChange, savedTestCases]);

        const runPrompt = useCallback(async () => {
            const userRequest = prompt.trim();
            if (!userRequest) return;

            inlineTestAbortRef.current?.abort();
            const abortController = new AbortController();
            inlineTestAbortRef.current = abortController;
            setInlineTesting(true);
            setThinking([]);
            setResponse("");
            setToolCalls([]);
            setInlineTestError(null);

            try {
                for await (const token of store.testSkillInline(
                    { skill, userRequest },
                    abortController.signal,
                )) {
                    if (token.thinkingMessage) {
                        setThinking((current) => [
                            ...current,
                            token.thinkingMessage!,
                        ]);
                    }
                    if (token.thinkingMessages?.length) {
                        setThinking((current) => [
                            ...current,
                            ...token.thinkingMessages!,
                        ]);
                    }
                    if (token.text) {
                        setResponse((current) => current + token.text);
                    }
                    if (token.functionCalls?.length) {
                        setToolCalls(token.functionCalls);
                    }
                    if (token.endMessage?.message) {
                        setThinking((current) => [
                            ...current,
                            token.endMessage!.message,
                        ]);
                    }
                }
            } catch (error) {
                if (!abortController.signal.aborted) {
                    setInlineTestError(
                        error instanceof Error
                            ? error.message
                            : "The skill test failed.",
                    );
                }
            } finally {
                if (inlineTestAbortRef.current === abortController) {
                    inlineTestAbortRef.current = null;
                    setInlineTesting(false);
                }
            }
        }, [prompt, skill, store]);

        const updateTestCase = (
            index: number,
            patch: Partial<AgentSkillTestCase>,
        ) => {
            const next = [...testCases];
            next[index] = { ...next[index], ...patch };
            onTestCasesChange(next);
            setTestResults(null);
        };

        const addTestCase = () => {
            onTestCasesChange([
                ...testCases,
                {
                    userRequest: "",
                    idealOutcome: "",
                    expectedToolCalls: "",
                },
            ]);
            setTestResults(null);
        };

        const removeTestCase = (index: number) => {
            onTestCasesChange(
                testCases.filter((_, testCaseIndex) => testCaseIndex !== index),
            );
            setTestResults(null);
        };

        const saveDataset = async () => {
            if (!skillId || !datasetValid) return;

            const saved = await store.saveTestDataset(skillId, testCases);
            if (!saved) return;

            const persistedTestCases = cloneTestCases(saved.testCases ?? []);
            setSavedTestCases(persistedTestCases);
            onTestCasesChange(cloneTestCases(persistedTestCases));
            onVersionSaved(saved);
        };

        const runDataset = async () => {
            const result = await store.testSkill({
                skill,
                testCases,
            });
            setTestResults(result ?? null);
        };

        return (
            <>
                <Dialog
                    open={open}
                    onClose={requestClose}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        sx: {
                            minHeight: { xs: "100%", sm: "76vh" },
                            maxHeight: { xs: "100%", sm: "90vh" },
                        },
                    }}
                >
                <DialogTitle sx={{ px: 3, py: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6">Test Skill</Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                            >
                                {skill.name || "Untitled skill"}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="Close skill test"
                            onClick={requestClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <Tabs
                    value={mode}
                    onChange={(_, value: TestMode) => setMode(value)}
                    sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
                >
                    <Tab value="prompt" label="Prompt" />
                    <Tab value="dataset" label="Dataset" />
                </Tabs>
                <DialogContent sx={{ p: 0 }}>
                    {mode === "prompt" ? (
                        <Stack spacing={3} sx={{ p: 3 }}>
                            <Box>
                                <AcxMainTextField
                                    id="agent-skill-test-prompt"
                                    labelText="Enter Prompt"
                                    multiline
                                    rowsMin={3}
                                    rowsMax={8}
                                    value={prompt}
                                    onChange={(event) =>
                                        setPrompt(event.target.value)
                                    }
                                    placeholderText="Ask KBo to use this skill..."
                                />
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1.5 }}
                                >
                                    <AcxButton
                                        color="primary"
                                        loading={inlineTesting}
                                        startIcon={
                                            <PlayArrowIcon fontSize="small" />
                                        }
                                        disabled={
                                            inlineTesting ||
                                            !canTestSkill ||
                                            !prompt.trim()
                                        }
                                        onClick={runPrompt}
                                    >
                                        Run Prompt
                                    </AcxButton>
                                    {inlineTesting && (
                                        <AcxButton
                                            acxTheme="secondary"
                                            startIcon={
                                                <StopCircleIcon fontSize="small" />
                                            }
                                            onClick={() =>
                                                inlineTestAbortRef.current?.abort()
                                            }
                                        >
                                            Stop
                                        </AcxButton>
                                    )}
                                </Stack>
                            </Box>

                            {inlineTestError && (
                                <Alert severity="error">
                                    {inlineTestError}
                                </Alert>
                            )}

                            <Box>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    Thinking
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={(theme) => ({
                                        p: 2,
                                        minHeight: 88,
                                        bgcolor: theme.palette.action.hover,
                                    })}
                                >
                                    {thinking.length > 0 ? (
                                        <Stack spacing={0.75}>
                                            {thinking.map((message, index) => (
                                                <Typography
                                                    key={`${index}-${message}`}
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {message}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Thinking and tool activity will
                                            appear here while the skill runs.
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>

                            {toolCalls.length > 0 && (
                                <Box>
                                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                                        Tool Calls
                                    </Typography>
                                    <Stack spacing={1}>
                                        {toolCalls.map((toolCall, index) => (
                                            <AgentSkillToolCallDisclosure
                                                key={`${index}-${getToolCallName(toolCall)}`}
                                                toolCall={toolCall}
                                                index={index}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            <Box>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    Skill Response
                                </Typography>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        minHeight: 120,
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {response || (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            The response will appear here.
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        </Stack>
                    ) : (
                        <Stack spacing={2} sx={{ p: 3 }}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                spacing={1}
                            >
                                <Box>
                                    <Typography fontWeight={600}>
                                        Dataset Evaluation
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Run repeatable requests and compare the
                                        results with expected outcomes.
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <AcxButton
                                        acxTheme="secondary"
                                        startIcon={
                                            <AddIcon fontSize="small" />
                                        }
                                        onClick={addTestCase}
                                    >
                                        Add Case
                                    </AcxButton>
                                    <AcxButton
                                        color="primary"
                                        loading={savingDataset}
                                        startIcon={
                                            <SaveIcon fontSize="small" />
                                        }
                                        disabled={
                                            !skillId ||
                                            !datasetDirty ||
                                            !datasetValid ||
                                            !hasScopes ||
                                            savingDataset
                                        }
                                        disabledTooltip={
                                            !skillId
                                                ? "Save the skill before saving its dataset."
                                                : !datasetValid
                                                  ? "Every case requires a user request and ideal outcome."
                                                  : !hasScopes
                                                    ? "Select at least one application scope before saving the dataset."
                                                  : undefined
                                        }
                                        onClick={saveDataset}
                                    >
                                        Save Dataset
                                    </AcxButton>
                                    <AcxButton
                                        color="primary"
                                        loading={testingDataset}
                                        startIcon={
                                            <PlayArrowIcon fontSize="small" />
                                        }
                                        disabled={
                                            testingDataset ||
                                            !canTestSkill ||
                                            testCases.length === 0
                                        }
                                        onClick={runDataset}
                                    >
                                        Run Dataset
                                    </AcxButton>
                                </Stack>
                            </Stack>

                            {testCases.length === 0 && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 4,
                                        textAlign: "center",
                                        borderStyle: "dashed",
                                    }}
                                >
                                    <Typography color="text.secondary">
                                        Add a test case to get started.
                                    </Typography>
                                </Paper>
                            )}

                            {testCases.map((testCase, index) => (
                                <Paper
                                    key={index}
                                    variant="outlined"
                                    sx={{ p: 2 }}
                                >
                                    <Stack spacing={2}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Typography fontWeight={600}>
                                                Case {index + 1}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                aria-label="Remove test case"
                                                onClick={() =>
                                                    removeTestCase(index)
                                                }
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                        <AcxMainTextField
                                            id={`agent-skill-test-user-request-${index}`}
                                            labelText="User Request"
                                            required
                                            multiline
                                            rowsMin={2}
                                            rowsMax={5}
                                            value={testCase.userRequest}
                                            onChange={(event) =>
                                                updateTestCase(index, {
                                                    userRequest:
                                                        event.target.value,
                                                })
                                            }
                                        />
                                        <AcxMainTextField
                                            id={`agent-skill-test-ideal-outcome-${index}`}
                                            labelText="Ideal Outcome"
                                            required
                                            multiline
                                            rowsMin={2}
                                            rowsMax={8}
                                            value={testCase.idealOutcome}
                                            onChange={(event) =>
                                                updateTestCase(index, {
                                                    idealOutcome:
                                                        event.target.value,
                                                })
                                            }
                                        />
                                        {testResults?.results
                                            ?.filter(
                                                (result) =>
                                                    result.index === index,
                                            )
                                            .map((result) => (
                                                <Alert
                                                    key={result.index}
                                                    severity={
                                                        result.passed
                                                            ? "success"
                                                            : "warning"
                                                    }
                                                >
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="body2">
                                                            {result.summary}
                                                        </Typography>
                                                        {(
                                                            result.suggestions ??
                                                            []
                                                        ).map((suggestion) => (
                                                            <Typography
                                                                key={
                                                                    suggestion
                                                                }
                                                                variant="body2"
                                                            >
                                                                {suggestion}
                                                            </Typography>
                                                        ))}
                                                    </Stack>
                                                </Alert>
                                            ))}
                                    </Stack>
                                </Paper>
                            ))}

                            {testResults && (
                                <Alert
                                    severity={
                                        testResults.failedCount > 0
                                            ? "warning"
                                            : "success"
                                    }
                                >
                                    {testResults.passedCount} passed,{" "}
                                    {testResults.failedCount} failed.
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                </Dialog>
                <AcxConfirmationDialog
                    isOpen={confirmDiscardOpen}
                    title="Discard unsaved dataset changes?"
                    content="You have unsaved dataset changes. Are you sure you want to close without saving them?"
                    confirmButtonText="Discard Changes"
                    cancelButtonText="Keep Editing"
                    onClose={() => setConfirmDiscardOpen(false)}
                    onConfirm={discardAndClose}
                />
            </>
        );
    },
);

export default AgentSkillTestDialog;
